import { useEffect, useMemo, useState } from 'react';
import { customDeviceList } from '../../../helpers/customDeviceList';
import { sessionApi } from '../../../helpers/sessionApi';

// Device catalogue for "Use Device Measurement" (Figma P3.1 Make / Device dropdowns).
// Source: GET /api/calibration/devices/?make= (BE-7) -> {makes:[...], models:[{id, make, model,
// device_id, screen_mm:[long, short], diagonal_mm, aspect_ratio}]}. The API was seeded from
// helpers/customDeviceList.js, which stays the offline fallback (API down, 404, not logged in).

// Same formula as helpers/useCustomDeviceDimensions (width = long edge, rounded to the mm), so the
// calibration maths do not change whichever source the model came from.
export const dimensionsFromFormula = (diagonalMm, aspectRatio) => {
  const width = Math.round(diagonalMm / Math.sqrt(Math.pow(aspectRatio, 2) + 1));
  const height = Math.round(aspectRatio * width);
  return { width, height };
};

const fromLocalModel = (make, m) => ({
  id: m.id,
  make,
  name: m.name,
  diagonalMm: m.diagonalMm,
  aspectRatio: m.aspectRatio,
  ...dimensionsFromFormula(m.diagonalMm, m.aspectRatio),
});

const fromApiModel = (m) => {
  const dims = m.diagonal_mm && m.aspect_ratio ? dimensionsFromFormula(m.diagonal_mm, m.aspect_ratio) : { width: Math.round(m.screen_mm?.[0]), height: Math.round(m.screen_mm?.[1]) };
  return { id: m.device_id || String(m.id), make: m.make, name: m.model, diagonalMm: m.diagonal_mm, aspectRatio: m.aspect_ratio, ...dims };
};

export const LOCAL_MAKES = customDeviceList.map((d) => d.make);
export const localModelsFor = (make) => (customDeviceList.find((d) => d.make === make)?.models || []).map((m) => fromLocalModel(make, m));

/**
 * useCalibrationDevices(make) -> { makes, models, source: 'api' | 'local', loading }
 * `models` are normalised to { id, make, name, diagonalMm, aspectRatio, width, height } (mm,
 * width = long edge) for the selected make.
 */
export const useCalibrationDevices = (make) => {
  const [makes, setMakes] = useState(LOCAL_MAKES);
  const [apiModels, setApiModels] = useState(null);
  const [source, setSource] = useState('local');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    sessionApi('api/calibration/devices/?make=__none__')
      .then((data) => {
        if (cancelled || !Array.isArray(data?.makes) || !data.makes.length) return;
        setMakes(data.makes);
        setSource('api');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!make || source !== 'api') {
      setApiModels(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    sessionApi(`api/calibration/devices/?make=${encodeURIComponent(make)}`)
      .then((data) => {
        if (cancelled) return;
        setApiModels(Array.isArray(data?.models) ? data.models.map(fromApiModel) : null);
      })
      .catch(() => !cancelled && setApiModels(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [make, source]);

  const models = useMemo(() => {
    if (!make) return [];
    if (apiModels && apiModels.length) return apiModels;
    return localModelsFor(make);
  }, [make, apiModels]);

  return { makes, models, source, loading };
};

export default useCalibrationDevices;
